<template>
  <vs-popup
    class="audio-presentation-popup"
    title="Áudio de apresentação"
    :active.sync="internalActive"
    :button-close-hidden="isUploading"
  >
    <div class="audio-presentation">
      <p class="audio-presentation-intro">
        Grave um áudio curto de até <strong>2 minutos</strong> se apresentando.
        Esse áudio fica no seu perfil e é exibido para todas as empresas em
        qualquer vaga que você se candidatar — não precisa gravar para cada
        oportunidade. Conte um pouco quem você é, sua trajetória e o que procura agora.
      </p>

      <div v-if="permissionError" class="audio-presentation-error">
        <p>
          Não conseguimos acessar o microfone. Verifique nas configurações do
          navegador se o acesso ao microfone está permitido para esta página.
        </p>
      </div>

      <div v-if="devices.length && state !== 'recording'" class="audio-presentation-device">
        <label for="audio-presentation-device" class="audio-presentation-device-label">
          Microfone
        </label>
        <select
          id="audio-presentation-device"
          v-model="selectedDeviceId"
          class="audio-presentation-device-select"
          :disabled="isUploading"
        >
          <option v-for="(device, index) in devices" :key="device.deviceId" :value="device.deviceId">
            {{ device.label || `Microfone ${index + 1}` }}
          </option>
        </select>
      </div>

      <div class="audio-presentation-stage">
        <div v-if="state === 'idle'" class="audio-presentation-idle">
          <button
            type="button"
            class="audio-presentation-record-btn"
            :disabled="permissionError"
            @click="startRecording"
          >
            <span class="audio-presentation-record-dot"></span>
            Iniciar gravação
          </button>
          <p v-if="hasExisting" class="audio-presentation-warning">
            Iniciar uma nova gravação substituirá o áudio salvo atualmente.
          </p>
        </div>

        <div v-else-if="state === 'recording'" class="audio-presentation-recording">
          <div class="audio-presentation-pulse"></div>
          <div class="audio-presentation-timer">
            {{ formatTime(elapsed) }} <span class="audio-presentation-timer-max">/ 02:00</span>
          </div>
          <div class="audio-presentation-meter">
            <div class="audio-presentation-meter-fill" :style="{ width: `${Math.round(level * 100)}%` }"></div>
          </div>
          <p v-if="elapsed >= SILENCE_HINT_SECONDS && !heardSound" class="audio-presentation-warning">
            Não estamos captando som. Pare a gravação e escolha outro microfone.
          </p>
          <button
            type="button"
            class="audio-presentation-stop-btn"
            @click="stopRecording"
          >
            Parar gravação
          </button>
        </div>

        <div v-else-if="state === 'recorded'" class="audio-presentation-recorded">
          <audio v-if="previewUrl" ref="preview" :src="previewUrl" controls class="audio-presentation-preview"></audio>
          <p class="audio-presentation-recorded-meta">
            Duração: {{ formatTime(recordedDuration) }}
          </p>
          <div v-if="!heardSound" class="audio-presentation-error">
            <p>
              O áudio ficou sem som. Confira se o microfone certo está selecionado
              acima e grave de novo.
            </p>
          </div>
          <div class="audio-presentation-actions">
            <button
              type="button"
              class="audio-presentation-secondary-btn"
              :disabled="isUploading"
              @click="discardAndRecordAgain"
            >
              Regravar
            </button>
            <button
              type="button"
              class="audio-presentation-primary-btn"
              :disabled="isUploading || !heardSound"
              @click="save"
            >
              {{ isUploading ? 'Salvando...' : 'Salvar áudio' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </vs-popup>
</template>

<script>
const MAX_DURATION_SECONDS = 120;
const SILENCE_HINT_SECONDS = 3;
// Pico abaixo de -40 dBFS durante a gravação inteira = microfone mudo ou dispositivo errado.
const SOUND_PEAK_THRESHOLD = 0.01;
const DEVICE_STORAGE_KEY = 'audio_presentation_device_id';

export default {
  name: 'AudioPresentationModal',
  props: {
    active: {
      type: Boolean,
      required: true,
    },
    hasExisting: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    state: 'idle', // idle | recording | recorded
    stream: null,
    recorder: null,
    chunks: [],
    blob: null,
    previewUrl: null,
    elapsed: 0,
    recordedDuration: 0,
    intervalId: null,
    permissionError: false,
    isUploading: false,
    devices: [],
    selectedDeviceId: '',
    level: 0,
    heardSound: false,
    audioContext: null,
    meterFrameId: null,
    SILENCE_HINT_SECONDS,
  }),
  computed: {
    internalActive: {
      get() {
        return this.active;
      },
      set(value) {
        this.$emit('update:active', value);
      },
    },
  },
  watch: {
    active(value) {
      if (value) {
        this.reset();
        this.loadDevices();
      } else {
        this.cleanup();
      }
    },
    selectedDeviceId(value) {
      try {
        if (value) localStorage.setItem(DEVICE_STORAGE_KEY, value);
      } catch (_) {}
    },
  },
  mounted() {
    if (navigator.mediaDevices && navigator.mediaDevices.addEventListener) {
      navigator.mediaDevices.addEventListener('devicechange', this.loadDevices);
    }
  },
  beforeDestroy() {
    if (navigator.mediaDevices && navigator.mediaDevices.removeEventListener) {
      navigator.mediaDevices.removeEventListener('devicechange', this.loadDevices);
    }
    this.cleanup();
  },
  methods: {
    reset() {
      this.cleanup();
      this.state = 'idle';
      this.elapsed = 0;
      this.recordedDuration = 0;
      this.permissionError = false;
      this.isUploading = false;
      this.heardSound = false;
      this.level = 0;
      this.blob = null;
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = null;
      }
    },
    cleanup() {
      // O popup só esconde o conteúdo ao fechar; sem pausar, a prévia segue tocando.
      if (this.$refs.preview) {
        this.$refs.preview.pause();
      }
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
      if (this.recorder && this.recorder.state !== 'inactive') {
        try {
          this.recorder.stop();
        } catch (_) {}
      }
      this.recorder = null;
      this.stopMeter();
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop());
        this.stream = null;
      }
    },
    async loadDevices() {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) return;
      try {
        const all = await navigator.mediaDevices.enumerateDevices();
        // Sem permissão o navegador devolve dispositivos sem nome e sem id; nesse caso
        // a lista só aparece depois da primeira gravação.
        this.devices = all.filter((device) => device.kind === 'audioinput' && device.deviceId);
      } catch (_) {
        this.devices = [];
        return;
      }

      const ids = this.devices.map((device) => device.deviceId);
      if (ids.includes(this.selectedDeviceId)) return;

      let saved = '';
      try {
        saved = localStorage.getItem(DEVICE_STORAGE_KEY) || '';
      } catch (_) {}
      if (ids.includes(saved)) {
        this.selectedDeviceId = saved;
      } else if (ids.includes('default')) {
        this.selectedDeviceId = 'default';
      } else {
        this.selectedDeviceId = ids[0] || '';
      }
    },
    startMeter() {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass || !this.stream) {
        // Sem Web Audio não dá para medir; não bloqueia o envio.
        this.heardSound = true;
        return;
      }
      try {
        this.audioContext = new AudioContextClass();
        if (this.audioContext.state === 'suspended') this.audioContext.resume();
        const source = this.audioContext.createMediaStreamSource(this.stream);
        const analyser = this.audioContext.createAnalyser();
        analyser.fftSize = 2048;
        source.connect(analyser);

        const useFloat = typeof analyser.getFloatTimeDomainData === 'function';
        const buffer = useFloat ? new Float32Array(analyser.fftSize) : new Uint8Array(analyser.fftSize);

        const tick = () => {
          let peak = 0;
          if (useFloat) {
            analyser.getFloatTimeDomainData(buffer);
            for (let i = 0; i < buffer.length; i++) peak = Math.max(peak, Math.abs(buffer[i]));
          } else {
            analyser.getByteTimeDomainData(buffer);
            for (let i = 0; i < buffer.length; i++) peak = Math.max(peak, Math.abs(buffer[i] - 128) / 128);
          }
          if (peak >= SOUND_PEAK_THRESHOLD) this.heardSound = true;
          // Barra de -60 dB a 0 dB.
          const db = peak > 0 ? 20 * Math.log10(peak) : -60;
          this.level = Math.min(1, Math.max(0, (db + 60) / 60));
          this.meterFrameId = requestAnimationFrame(tick);
        };
        tick();
      } catch (_) {
        this.heardSound = true;
        this.stopMeter();
      }
    },
    stopMeter() {
      if (this.meterFrameId) {
        cancelAnimationFrame(this.meterFrameId);
        this.meterFrameId = null;
      }
      if (this.audioContext) {
        try {
          this.audioContext.close();
        } catch (_) {}
        this.audioContext = null;
      }
      this.level = 0;
    },
    async startRecording() {
      this.permissionError = false;
      try {
        const audio = this.selectedDeviceId ? { deviceId: { exact: this.selectedDeviceId } } : true;
        try {
          this.stream = await navigator.mediaDevices.getUserMedia({ audio });
        } catch (error) {
          // Microfone salvo foi desconectado: volta para o padrão do navegador.
          if (error && error.name === 'OverconstrainedError') {
            this.selectedDeviceId = '';
            this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          } else {
            throw error;
          }
        }
      } catch (error) {
        this.permissionError = true;
        return;
      }

      this.loadDevices();

      const mime = this.pickSupportedMimeType();
      try {
        this.recorder = mime
          ? new MediaRecorder(this.stream, { mimeType: mime })
          : new MediaRecorder(this.stream);
      } catch (error) {
        this.permissionError = true;
        this.cleanup();
        return;
      }

      this.chunks = [];
      this.elapsed = 0;
      this.heardSound = false;
      this.state = 'recording';
      this.startMeter();

      this.recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          this.chunks.push(event.data);
        }
      };
      this.recorder.onstop = () => {
        const type = (this.recorder && this.recorder.mimeType) || 'audio/webm';
        this.blob = new Blob(this.chunks, { type });
        if (this.previewUrl) {
          URL.revokeObjectURL(this.previewUrl);
        }
        this.previewUrl = URL.createObjectURL(this.blob);
        this.recordedDuration = this.elapsed;
        this.state = 'recorded';
        this.stopMeter();
        if (this.stream) {
          this.stream.getTracks().forEach((track) => track.stop());
          this.stream = null;
        }
      };
      this.recorder.start();

      this.intervalId = setInterval(() => {
        this.elapsed += 1;
        if (this.elapsed >= MAX_DURATION_SECONDS) {
          this.stopRecording();
        }
      }, 1000);
    },
    stopRecording() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
      if (this.recorder && this.recorder.state !== 'inactive') {
        try {
          this.recorder.stop();
        } catch (_) {}
      }
    },
    discardAndRecordAgain() {
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = null;
      }
      this.blob = null;
      this.elapsed = 0;
      this.recordedDuration = 0;
      this.heardSound = false;
      this.state = 'idle';
    },
    async save() {
      if (!this.blob || this.isUploading) return;
      this.isUploading = true;
      try {
        const ext = this.extensionFromMime(this.blob.type);
        const filename = `presentation.${ext}`;
        const file = new File([this.blob], filename, { type: this.blob.type });
        await this.$store.dispatch('presentation/saveAudio', file);
        this.$vs.notify({
          title: 'Áudio salvo!',
          text: 'Seu áudio de apresentação foi atualizado.',
          iconPack: 'feather',
          icon: 'icon-check-circle',
          color: 'success',
        });
        this.$emit('saved');
        this.internalActive = false;
      } catch (error) {
        const message =
          (error && error.response && error.response.data && error.response.data.message) ||
          'Não foi possível salvar o áudio. Tente novamente.';
        this.$vs.notify({
          title: 'Falha ao salvar',
          text: message,
          iconPack: 'feather',
          icon: 'icon-x-circle',
          color: 'danger',
        });
      } finally {
        this.isUploading = false;
      }
    },
    formatTime(totalSeconds) {
      const seconds = Math.max(0, Math.floor(totalSeconds || 0));
      const m = Math.floor(seconds / 60);
      const s = seconds % 60;
      return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    },
    pickSupportedMimeType() {
      if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
        return null;
      }
      const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/ogg', 'audio/mp4'];
      for (const candidate of candidates) {
        if (MediaRecorder.isTypeSupported(candidate)) return candidate;
      }
      return null;
    },
    extensionFromMime(mime) {
      if (!mime) return 'webm';
      if (mime.includes('webm')) return 'webm';
      if (mime.includes('ogg')) return 'ogg';
      if (mime.includes('mp4')) return 'm4a';
      if (mime.includes('mpeg')) return 'mp3';
      if (mime.includes('wav')) return 'wav';
      return 'webm';
    },
  },
};
</script>

<style lang="scss">
.audio-presentation-popup {
  .vs-popup {
    max-width: 520px;
    width: 100%;
    border-radius: 16px;
  }
  .vs-popup--content {
    padding: 24px;
  }
}

.audio-presentation {
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: Roboto, sans-serif;
  color: #333;

  &-intro {
    font-size: 14px;
    line-height: 1.5;
    color: #4f4f4f;
    margin: 0;

    strong {
      color: #333;
    }
  }

  &-error {
    background: rgba(222, 35, 72, 0.08);
    border: 0.5px solid #de2348;
    border-radius: 8px;
    padding: 12px;
    color: #de2348;
    font-size: 14px;
  }

  &-device {
    display: flex;
    flex-direction: column;
    gap: 6px;

    &-label {
      font-size: 14px;
      font-weight: 500;
      color: #333;
    }

    &-select {
      height: 40px;
      padding: 0 12px;
      border-radius: 8px;
      border: 0.5px solid #a8a8a8;
      background: #fff;
      color: #333;
      font-size: 14px;
      font-family: inherit;

      &:disabled {
        opacity: 0.5;
      }
    }
  }

  &-meter {
    width: 200px;
    height: 6px;
    border-radius: 3px;
    background: #ecebeb;
    overflow: hidden;

    &-fill {
      height: 100%;
      background: #4169e1;
      transition: width 0.08s linear;
    }
  }

  &-stage {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 24px;
    border-radius: 16px;
    background: #f7f8fa;
    border: 1px solid #ecebeb;
    min-height: 180px;
    justify-content: center;
  }

  &-idle {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }

  &-record-btn {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    height: 44px;
    padding: 0 20px;
    border-radius: 22px;
    background: #4169e1;
    color: #fff;
    font-weight: 500;
    font-size: 15px;
    border: none;
    cursor: pointer;
    transition: filter 0.2s ease;

    &:hover:not(:disabled) {
      filter: brightness(0.95);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &-record-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #fff;
  }

  &-warning {
    margin: 0;
    font-size: 13px;
    color: #af6c00;
    text-align: center;
  }

  &-recording {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }

  &-pulse {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: #de2348;
    animation: audio-presentation-pulse 1.4s ease-in-out infinite;
  }

  &-timer {
    font-size: 24px;
    font-weight: 600;
    color: #333;
    font-variant-numeric: tabular-nums;

    &-max {
      color: #696969;
      font-weight: 400;
      font-size: 18px;
    }
  }

  &-stop-btn {
    height: 40px;
    padding: 0 18px;
    border-radius: 8px;
    background: #fff;
    color: #de2348;
    border: 1px solid #de2348;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s ease;

    &:hover {
      background: rgba(222, 35, 72, 0.08);
    }
  }

  &-recorded {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  &-preview {
    width: 100%;
  }

  &-recorded-meta {
    margin: 0;
    color: #696969;
    font-size: 13px;
    text-align: center;
  }

  &-actions {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
    flex-wrap: wrap;
  }

  &-secondary-btn,
  &-primary-btn {
    height: 40px;
    padding: 0 16px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: filter 0.2s ease;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &-secondary-btn {
    background: #fff;
    color: #4f4f4f;
    border: 0.5px solid #a8a8a8;
  }

  &-primary-btn {
    background: #4169e1;
    color: #fff;
    border: none;

    &:hover:not(:disabled) {
      filter: brightness(0.95);
    }
  }
}

@keyframes audio-presentation-pulse {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(222, 35, 72, 0.4);
  }
  70% {
    transform: scale(1.05);
    box-shadow: 0 0 0 16px rgba(222, 35, 72, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(222, 35, 72, 0);
  }
}
</style>
